/**
 * Client-side HTML sanitization utilities
 * Uses DOMPurify to prevent XSS attacks when rendering user-generated HTML
 */

import DOMPurify from 'dompurify';

/**
 * Sanitization configuration for rich text content (blog posts, announcements)
 * Allows safe HTML tags while removing potentially dangerous content
 */
const RICH_TEXT_CONFIG = {
  ALLOWED_TAGS: [
    // Text formatting
    'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'del',
    // Headings
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    // Lists
    'ul', 'ol', 'li',
    // Links and media
    'a', 'img',
    // Block elements
    'blockquote', 'pre', 'code', 'hr', 'div', 'span',
    // Tables (if needed)
    'table', 'thead', 'tbody', 'tr', 'th', 'td',
  ],
  ALLOWED_ATTR: [
    'href', 'target', 'rel', 'src', 'alt', 'title', 'class', 'id',
    'width', 'height', 'style'
  ],
  // Force all links to open in new tab with security attributes
  ADD_ATTR: ['target', 'rel'],
  // Prevent dangerous protocols
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
  // Remove scripts entirely
  FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'button'],
  FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur'],
};

/**
 * Sanitize rich text HTML content (blog posts, announcements)
 * Removes XSS vectors while preserving safe formatting
 */
export function sanitizeRichText(html: string): string {
  if (!html || typeof html !== 'string') return '';
  
  // Configure DOMPurify to add security attributes to links
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A') {
      // Force external links to open in new tab with security
      const href = node.getAttribute('href');
      if (href && (href.startsWith('http://') || href.startsWith('https://'))) {
        node.setAttribute('target', '_blank');
        node.setAttribute('rel', 'noopener noreferrer');
      }
    }
    // Remove any javascript: URLs that might have slipped through
    if (node.hasAttribute('href')) {
      const href = node.getAttribute('href');
      if (href && href.toLowerCase().startsWith('javascript:')) {
        node.removeAttribute('href');
      }
    }
  });
  
  const sanitized = DOMPurify.sanitize(html, RICH_TEXT_CONFIG);
  
  // Remove the hook to prevent memory leaks
  DOMPurify.removeHook('afterSanitizeAttributes');
  
  // DOMPurify.sanitize returns string when RETURN_DOM is not set
  return sanitized as string;
}

/**
 * Sanitize plain text - escapes all HTML entities
 * Use for user names, emails, etc. that should never contain HTML
 */
export function sanitizePlainText(text: string): string {
  if (!text || typeof text !== 'string') return '';
  
  const htmlEntities: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
    '/': '&#x2F;',
  };
  
  return text.replace(/[&<>"'/]/g, (char) => htmlEntities[char] || char);
}

/**
 * Validate and sanitize URL
 * Returns empty string if URL is invalid or potentially dangerous
 */
export function sanitizeUrl(url: string): string {
  if (!url || typeof url !== 'string') return '';
  
  try {
    const parsed = new URL(url);
    // Only allow safe protocols
    if (!['http:', 'https:', 'mailto:', 'tel:'].includes(parsed.protocol)) {
      return '';
    }
    return url;
  } catch {
    // If URL parsing fails, it might be a relative URL - check for javascript:
    if (url.toLowerCase().startsWith('javascript:')) {
      return '';
    }
    return url;
  }
}
